import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Language = 'en' | 'it';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

// IndexedDB utilities
const DB_NAME = 'ZetaAdminDB';
const DB_VERSION = 1;
const STORE_NAME = 'settings';

// Clear IndexedDB and start fresh (development helper)
const clearIndexedDB = async (): Promise<void> => {
  try {
    indexedDB.deleteDatabase(DB_NAME);
    console.log('IndexedDB cleared, will use localStorage fallback');
  } catch (error) {
    console.error('Failed to clear IndexedDB:', error);
  }
};

// Development helper - expose clear function to window for debugging
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).clearZetaIndexedDB = clearIndexedDB;
}

const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
  });
};

const saveToIndexedDB = async (key: string, value: any): Promise<void> => {
  try {
    const db = await openDB();
    
    // Check if the object store exists
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      console.warn('Object store does not exist, falling back to localStorage');
      localStorage.setItem(key, JSON.stringify(value));
      return;
    }
    
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    store.put({ key, value });
  } catch (error) {
    console.error('Failed to save to IndexedDB:', error);
    // Fallback to localStorage
    localStorage.setItem(key, JSON.stringify(value));
  }
};

const loadFromIndexedDB = async (key: string): Promise<any> => {
  try {
    const db = await openDB();
    
    // Check if the object store exists
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      console.warn('Object store does not exist, falling back to localStorage');
      const fallback = localStorage.getItem(key);
      return fallback ? JSON.parse(fallback) : null;
    }
    
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(key);
    
    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        resolve(request.result?.value);
      };
      request.onerror = () => {
        // Fallback to localStorage
        const fallback = localStorage.getItem(key);
        resolve(fallback ? JSON.parse(fallback) : null);
      };
    });
  } catch (error) {
    console.error('Failed to load from IndexedDB:', error);
    // Fallback to localStorage
    const fallback = localStorage.getItem(key);
    return fallback ? JSON.parse(fallback) : null;
  }
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('it'); // Default to Italian
  const [translations, setTranslations] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState(true);

  // Load saved language from IndexedDB
  useEffect(() => {
    const loadSavedLanguage = async () => {
      try {
        const savedLanguage = await loadFromIndexedDB('zeta-admin-language');
        if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'it')) {
          setLanguageState(savedLanguage);
        } else {
          // Try localStorage as fallback
          const fallbackLanguage = localStorage.getItem('zeta-admin-language');
          if (fallbackLanguage && (fallbackLanguage === 'en' || fallbackLanguage === 'it')) {
            setLanguageState(fallbackLanguage);
          }
          // If no saved language, default to Italian ('it')
        }
      } catch (error) {
        console.error('Failed to load saved language:', error);
        // Try localStorage as fallback
        try {
          const fallbackLanguage = localStorage.getItem('zeta-admin-language');
          if (fallbackLanguage && (fallbackLanguage === 'en' || fallbackLanguage === 'it')) {
            setLanguageState(fallbackLanguage);
          }
        } catch (fallbackError) {
          console.error('Failed to load from localStorage fallback:', fallbackError);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadSavedLanguage();
  }, []);

  // Load translations
  useEffect(() => {
    if (isLoading) return;

    const loadTranslations = async () => {
      try {
        const translationModule = await import(`../locales/${language}.json`);
        setTranslations(translationModule.default);
      } catch (error) {
        console.error('Failed to load translations:', error);
        // Fallback to Italian if loading fails
        if (language !== 'it') {
          const fallbackModule = await import('../locales/it.json');
          setTranslations(fallbackModule.default);
        }
      }
    };

    loadTranslations();
  }, [language, isLoading]);

  const setLanguage = async (lang: Language) => {
    setLanguageState(lang);
    // Save to both IndexedDB and localStorage as backup
    await saveToIndexedDB('zeta-admin-language', lang);
    localStorage.setItem('zeta-admin-language', lang);
  };

  // Translation function with parameter replacement
  const t = (key: string, params?: Record<string, string | number>): string => {
    const keys = key.split('.');
    let value: any = translations;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        console.warn(`Translation key not found: ${key}`);
        return key; // Return the key if translation not found
      }
    }

    if (typeof value !== 'string') {
      console.warn(`Translation value is not a string: ${key}`);
      return key;
    }

    // Replace parameters in the translation string
    if (params) {
      return value.replace(/\{(\w+)\}/g, (match, paramKey) => {
        return params[paramKey]?.toString() || match;
      });
    }

    return value;
  };

  // Show loading state while language is being loaded
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
